import { Typography } from "@circle-react-uikit/Typography";
import { nameToInitials } from "@circle-react-uikit/UserImage/name";
import { classNames } from "@circle-react/helpers/twMergeWithCN";

import { CircleLogo } from "@/react/components/OnboardingComponents/CircleLogo";
import { getCommunityLogoUrlAndType } from "@/react/helpers/communityHelpers";
import { cssAnchorProps } from "@/react/helpers/cssAnchorHelpers";
import { useThemeContext } from "@/react/providers/Theme/ThemeContext";
import type { CurrentCommunity } from "@/react/types/CurrentCommunity";

interface CommunityImageProps {
  community?: CurrentCommunity;
  displayCommunityName?: boolean;
  isLeftAlignLogo?: boolean;
  withCircleBranding?: boolean;
}

const EMPTY_LOGO_DETAILS = {
  url: undefined,
  type: undefined,
};

export const CommunityImage = ({
  community,
  withCircleBranding = false,
  isLeftAlignLogo = false,
  displayCommunityName = true,
}: CommunityImageProps) => {
  const { isDarkAppearance } = useThemeContext();

  const { url, type } = community
    ? getCommunityLogoUrlAndType(community, isDarkAppearance)
    : EMPTY_LOGO_DETAILS;

  const brandColor = community?.prefs?.brand_color;
  const color =
    (isDarkAppearance ? brandColor?.dark : brandColor?.light) || "#506cf0";

  if (!type || withCircleBranding) {
    return <CircleLogo />;
  }

  if (type === "icon" && !url) {
    return (
      <div className="flex flex-col items-center">
        <div
          className={classNames(
            "mb-3 flex size-12 items-center justify-center rounded-md text-center",
            {
              "self-start": isLeftAlignLogo,
            },
          )}
          style={{ backgroundColor: color }}
          aria-hidden
        >
          <Typography.TitleSm weight="semibold" color="text-brand-button">
            {nameToInitials(community?.name)}
          </Typography.TitleSm>
        </div>
        {displayCommunityName && (
          <Typography.LabelMd weight="medium">
            {community?.name}
          </Typography.LabelMd>
        )}
      </div>
    );
  }

  if (type === "icon" && url) {
    return (
      <div className="flex flex-col items-center">
        <img
          loading="lazy"
          src={url}
          alt=""
          className={classNames("mb-3 h-8", {
            "self-start": isLeftAlignLogo,
          })}
          {...cssAnchorProps({ anchor: "app-auth-logo-image" })}
        />
        {displayCommunityName && (
          <Typography.LabelMd weight="medium">
            {community?.name}
          </Typography.LabelMd>
        )}
      </div>
    );
  }

  return (
    <img
      loading="lazy"
      className="max-h-9 max-w-[14.375rem]"
      src={url || undefined}
      alt={community?.name ?? ""}
      {...cssAnchorProps({ anchor: "app-auth-logo-image" })}
    />
  );
};
